import { memo, type ReactNode, type RefObject } from 'react';

import { Flex } from '@bonusly/design-system';

import type { ApprovalDecision, ChatMessage } from '../../../lib/bizy/types';
import { DocumentRenderer } from '../../bizy/renderers';
import { isNotesEdit } from '../lib/notes-edit-detection';
import type { BizyUiBlockActionDispatcher } from '../lib/ui-block-action-dispatcher';
import type { BizyKitRendererProps } from '../lib/use-bizy-kit-renderer-props';
import { BizyMessageFeedback } from './bizy-message-feedback';
import { BizyNotesEditMessage } from './bizy-notes-edit-message';
import { BizyStreamingMessage } from './bizy-streaming-message';
import { BizyTimeDivider } from './bizy-time-divider';
import { BizyUserMessage, type BizyUserMessageProps } from './bizy-user-message';
import { BizyUserMessageNodes } from './bizy-user-message-nodes';

export interface BizyTimelineMessageProps {
	message: ChatMessage;
	/**
	 * The message belongs to a conversation the user idled away from
	 * (BIZY-683), rendered above a fresh-start divider: identical visuals,
	 * but read-only — no feedback toolbar and no card follow-ups/confirms,
	 * since those act on the live conversation.
	 */
	archived: boolean;
	/** This message is the assistant turn currently streaming in. */
	isStreamingMessage: boolean;
	/** The newest complete assistant reply (feedback toolbar emphasis). */
	isLatestReply: boolean;
	/** Send-scroll anchor, attached to the last live user turn only. */
	anchorRef?: RefObject<HTMLDivElement>;
	/** Burst-break timestamp divider rendered above the message (BIZY-638). */
	dividerTimestamp?: Date;

	// Display config.
	chatTextFontSize?: BizyUserMessageProps['fontSize'];
	fullscreenFontSize?: 'sm' | 'md';
	conversationId?: string;
	hideSources: boolean;
	/** Live tool-call progress lines; shown only while this message streams. */
	toolProgress: string[];
	thinkingPhrase?: string;
	followUpsDisabled: boolean;

	// Renderer plumbing.
	rendererProps: BizyKitRendererProps;
	dispatchUiBlockAction: BizyUiBlockActionDispatcher;
	onSendChatMessage: (content: string) => void;
	onChoiceUpdate: (messageKey: string, cardIndex: number, choice: ApprovalDecision) => void;
	onSuggestedPageClick: (path: string) => void;

	// Feedback.
	onPositiveFeedback: (messageId: string) => void;
	onNegativeFeedback: (messageId: string) => void;
	onClearFeedback: (messageId: string) => void;
}

/** One timeline turn: optional burst-break divider plus the message body. */
function BizyTimelineMessageImpl({
	message,
	archived,
	isStreamingMessage,
	isLatestReply,
	anchorRef,
	dividerTimestamp,
	chatTextFontSize,
	fullscreenFontSize,
	conversationId,
	hideSources,
	toolProgress,
	thinkingPhrase,
	followUpsDisabled,
	rendererProps,
	dispatchUiBlockAction,
	onSendChatMessage,
	onChoiceUpdate,
	onSuggestedPageClick,
	onPositiveFeedback,
	onNegativeFeedback,
	onClearFeedback,
}: BizyTimelineMessageProps) {
	const isUser = message.role === 'user';
	const isComplete = !isStreamingMessage && !isUser && !!message.id;

	// Timestamp divider preceding a burst break (BIZY-638) — same
	// line—text—line treatment as every other timeline partition.
	const divider = dividerTimestamp ? <BizyTimeDivider timestamp={dividerTimestamp} /> : null;

	const feedback = !archived && isComplete && message.id && (
		<BizyMessageFeedback
			messageId={message.id}
			content={message.content}
			feedbackType={message.feedbackType}
			onPositiveFeedback={onPositiveFeedback}
			onNegativeFeedback={onNegativeFeedback}
			onClearFeedback={onClearFeedback}
			isLatestReply={isLatestReply}
		/>
	);

	let body: ReactNode;
	if (isUser) {
		// Bubbles sent moments ago pop in with a springy bounce; older
		// bubbles (e.g. when loading a conversation's history) render
		// instantly so the whole transcript doesn't animate on mount.
		const isFreshUserMessage = message.timestamp ? Date.now() - new Date(message.timestamp).getTime() < 1500 : false;
		body = message.nodes?.length ? (
			<BizyUserMessageNodes anchorRef={anchorRef} nodes={message.nodes} fontSize={chatTextFontSize} />
		) : (
			<BizyUserMessage
				ref={anchorRef}
				content={message.content}
				attachmentPreviewUrl={message.attachmentPreviewUrl}
				timestamp={message.timestamp}
				isFresh={isFreshUserMessage}
				fontSize={chatTextFontSize}
			/>
		);
	} else if (!isStreamingMessage && isNotesEdit(message)) {
		body = (
			<Flex direction="column" alignSelf="flex-start" maxWidth="100%" role="group">
				<BizyNotesEditMessage content={message.content} StreamingMessage={BizyStreamingMessage} />
				{feedback}
			</Flex>
		);
	} else {
		const messageToolProgress = isStreamingMessage ? toolProgress : [];
		const documentNodes = message.assistantMessage ?? [];

		body = (
			<Flex direction="column" alignSelf="flex-start" maxWidth="100%" role="group">
				{documentNodes.length > 0 ? (
					<DocumentRenderer
						{...rendererProps}
						dispatchUiBlockAction={dispatchUiBlockAction}
						fontSize={fullscreenFontSize}
						nodes={documentNodes}
						isComplete={!isStreamingMessage}
						isReadOnly={archived}
						messageKey={message.key}
						conversationId={conversationId}
						toolProgress={messageToolProgress}
						thinkingPhrase={thinkingPhrase}
						nodeChoices={message.nodeChoices}
						onConfirm={archived ? undefined : (_cardIndex, confirmMessage) => onSendChatMessage(confirmMessage)}
						onChoiceUpdate={archived ? undefined : onChoiceUpdate}
						onSuggestedPageClick={onSuggestedPageClick}
						onSelectFollowUp={archived ? undefined : onSendChatMessage}
						onDrill={archived ? undefined : onSendChatMessage}
						followUpsDisabled={archived || followUpsDisabled}
					/>
				) : (
					// Fallback for hydrated messages where assistantMessage is
					// absent (e.g. pre-backfill historical rows or proactive
					// notifications). Renders the plaintext bizy_response as
					// markdown. Live-streamed messages always populate
					// assistantMessage via the node-event handlers, so this
					// branch only fires on reload.
					<BizyStreamingMessage
						content={message.content}
						fontSize={fullscreenFontSize}
						isComplete={!isStreamingMessage}
						toolProgress={messageToolProgress}
						thinkingPhrase={thinkingPhrase}
						onSuggestedPageClick={onSuggestedPageClick}
						hideSources={hideSources}
					/>
				)}
				{feedback}
			</Flex>
		);
	}

	return (
		<>
			{divider}
			{body}
		</>
	);
}

// Memoized so a timeline-wide re-render (every streaming token bumps the parent
// and re-derives the `messages` array) only re-renders the turns whose props
// actually changed — in practice just the streaming turn. Relies on all shared
// props being referentially stable (see `timelineMessageSharedProps`'s useMemo
// and the `toFlat` identity cache); an unstable prop silently defeats this.
//
// A plain shallow `memo` is too brittle here: the renderer/handler props
// (`rendererProps`, `dispatchUiBlockAction`, the callbacks) are threaded from a
// chain of package hooks (`useHttpClient`, `useDispatch`, `useDialogControl`,
// mutation objects, …) that give no referential-stability guarantee, so their
// identity churns on unrelated renders even though their behavior is constant
// for the conversation. This comparator instead compares the props that
// actually change THIS row's rendered output and treats the handler bundle as
// stable-by-contract.
//
// Compared (a real change re-renders the row): the message object itself
// (identity-stabilized upstream via the `toFlat` WeakMap cache), the
// streaming/anchor/latest flags, display config, `conversationId`, and
// `followUpsDisabled`. Live progress (`toolProgress`/`thinkingPhrase`) only
// renders on the streaming row, so it's compared only there.
//
// NOT compared: the renderer/handler bundle. Trade-off — a mid-conversation
// change to renderer *data* (e.g. gift-wrap inventory) won't reflect on
// already-rendered cards until the next real render (a new/edited turn, feedback
// click, fullscreen toggle, …). Rare and self-healing; worth it to stop the
// per-token re-render storm across the whole transcript.
function bizyTimelineMessagePropsEqual(prev: BizyTimelineMessageProps, next: BizyTimelineMessageProps): boolean {
	const dataEqual =
		prev.message === next.message &&
		prev.archived === next.archived &&
		prev.isStreamingMessage === next.isStreamingMessage &&
		prev.isLatestReply === next.isLatestReply &&
		prev.anchorRef === next.anchorRef &&
		prev.dividerTimestamp === next.dividerTimestamp &&
		prev.chatTextFontSize === next.chatTextFontSize &&
		prev.fullscreenFontSize === next.fullscreenFontSize &&
		prev.conversationId === next.conversationId &&
		prev.hideSources === next.hideSources &&
		prev.followUpsDisabled === next.followUpsDisabled;

	// Live tool progress + thinking phrase only affect the streaming row's output.
	const streamingEqual =
		!prev.isStreamingMessage && !next.isStreamingMessage ? true : prev.toolProgress === next.toolProgress && prev.thinkingPhrase === next.thinkingPhrase;

	return dataEqual && streamingEqual;
}

export const BizyTimelineMessage = memo(BizyTimelineMessageImpl, bizyTimelineMessagePropsEqual);
